/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Button, IconClose } from '../../components';

import css from './Modal.module.css';

const KEY_ESCAPE = 'Escape';
const PAGE_ROOT_ID = 'root';
const PORTAL_ROOT_ID = 'portal-root';
const FOCUSABLE =
  'a[href], button, input, select, textarea, iframe, summary, [contenteditable], [tabindex]';
const NAMING_HEADING = 'h1, h2, h3, [role="heading"]';

// A <summary> is a stop only as the first one of its <details>, an editing host unless it has
// contenteditable="false"; an explicit negative tabindex removes either (jsdom gives them no tabIndex)
const isTabbableKind = el =>
  el.hasAttribute('tabindex')
    ? el.tabIndex >= 0
    : el.tagName === 'SUMMARY'
    ? el.parentElement?.tagName === 'DETAILS' && el.parentElement.querySelector('summary') === el
    : el.hasAttribute('contenteditable')
    ? el.getAttribute('contenteditable') !== 'false'
    : el.tabIndex >= 0;

// Tab stops of a dialog in DOM order; of a native radio group only the checked radio is a stop
const tabStops = dialog =>
  Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(
    el =>
      !el.disabled &&
      isTabbableKind(el) &&
      el.getClientRects().length > 0 &&
      !(
        el.type === 'radio' &&
        !el.checked &&
        dialog.querySelector(`input[type="radio"][name="${el.name}"]:checked`)
      )
  );

// Portal dialogs that keep the page behind inert. A nested dialog closing must not release the page
// while an outer one is still open, so the page is released when the last one closes (#26).
const inertHolders = new Set();
const holdPage = modal => {
  inertHolders.add(modal);
  document.getElementById(PAGE_ROOT_ID)?.setAttribute('inert', '');
};
const releasePage = modal => {
  inertHolders.delete(modal);
  if (inertHolders.size === 0) {
    document.getElementById(PAGE_ROOT_ID)?.removeAttribute('inert');
  }
};

// The main landmark takes the focus the way the skip link gives it (Topbar): tabindex -1 only for
// the moment, removed on blur; no scroll jump
const focusMainContent = () => {
  const main = document.getElementById('main-content') || document.querySelector('main');
  if (!main) {
    return;
  }
  if (!main.hasAttribute('tabindex')) {
    main.setAttribute('tabindex', '-1');
    main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
  }
  main.focus({ preventScroll: true });
};

class Portal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

/**
 * Modal
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.scrollLayerClassName overwrite components own css.scrollLayer
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string?} props.contentClassName overwrite components own css.content
 * @param {string?} props.isClosedClassName overwrite components own css.isClosed
 * @param {ReactNode} props.closeButtonMessage
 * @param {Object} props.lightCloseButton use lighter close button styles
 * @param {string} props.id
 * @param {Object} props.intl
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling
 * @param {boolean} props.usePortal
 * @param {string?} props.ariaLabelledBy id of the element that names the dialog (portal mode);
 *   without it the dialog is named by its first heading, then by ariaLabel, then by Modal.defaultLabel
 * @param {string?} props.ariaLabel name of a dialog without a heading (portal mode)
 * @param {string?} props.focusElementId id of the element focused on close; default: the element focused on open,
 *   and the main landmark for a dialog without one (opened on page load)
 * @param {boolean?} props.closeOnOutsideClick close on a click outside the dialog (portal mode); opt-in,
 *   never for a dialog with a form (the input would be lost)
 * @returns {JSX.Element} Modal element
 */
export class ModalComponent extends Component {
  constructor(props) {
    super(props);
    this.handleBodyKeyUp = this.handleBodyKeyUp.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleKeyDownInDialog = this.handleKeyDownInDialog.bind(this);
    this.handleBackdropMouseDown = this.handleBackdropMouseDown.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.updateDialogName = this.updateDialogName.bind(this);

    this.refDiv = React.createRef();
    this.refContent = React.createRef();
    this.vh = null;
    this.opener = null;
    this.contentObserver = null;
    this.pressedOnBackdrop = false;

    this.state = {
      portalRoot: null,
      headingName: null,
    };
  }

  componentDidMount() {
    const { id, isOpen, onManageDisableScrolling, usePortal } = this.props;
    onManageDisableScrolling(id, isOpen);
    if (usePortal && isOpen) {
      holdPage(this);
    }
    window.document.body.addEventListener('keyup', this.handleBodyKeyUp);

    // A hack to update container height for mobile Safari,
    // when resizing happens due to scroll.
    // css.isOpenInPortal has "height: calc(var(--vh, 1vh) * 100)"
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
    window.addEventListener('resize', this.handleResize);

    this.setState({
      portalRoot: document.getElementById(PORTAL_ROOT_ID),
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { id, isOpen, onManageDisableScrolling, focusElementId } = prevProps;
    if (this.props.isOpen !== isOpen) {
      onManageDisableScrolling(id, this.props.isOpen);

      // Because we are using portal,
      // we need to set the focus inside Modal manually
      // (ModalInMobile drops usePortal together with isOpen, hence prevProps on close)
      if (this.props.isOpen && this.props.usePortal) {
        this.openDialog();
      } else if (!this.props.isOpen && prevProps.usePortal) {
        this.closeDialog(focusElementId);
      }
    } else if (this.props.usePortal && this.props.isOpen && !prevState.portalRoot) {
      // Opened on mount: the dialog exists from the render after the portal root is known
      this.openDialog();
    }
  }

  componentWillUnmount() {
    const { id, onManageDisableScrolling, focusElementId } = this.props;
    window.document.body.removeEventListener('keyup', this.handleBodyKeyUp);
    window.document.body.removeEventListener('resize', this.handleResize);
    this.stopDialogListeners();
    onManageDisableScrolling(id, false);
    releasePage(this);
    if (focusElementId) {
      document.getElementById(focusElementId)?.focus();
    }
  }

  // Modal dialog pattern (D42): focus moves into the dialog, the page behind is inert, Tab and
  // Shift+Tab cycle inside, and the dialog is named by its heading while it is open.
  openDialog() {
    const active = document.activeElement;
    this.opener = active && active !== document.body ? active : null;
    // No scroll: below 768px the portal dialog is position: absolute at the end of the document, and
    // focusing it scrolled the page there — the scroll lock then kept that position (the page jumped on
    // open and closing scrolled to the bottom)
    this.refDiv.current?.focus({ preventScroll: true });
    holdPage(this);
    document.addEventListener('keydown', this.handleKeyDownInDialog);
    this.updateDialogName();
    // Content can arrive after the dialog opens (hosted pages, loadable chunks)
    if (typeof MutationObserver !== 'undefined' && this.refContent.current) {
      this.contentObserver = new MutationObserver(this.updateDialogName);
      this.contentObserver.observe(this.refContent.current, { childList: true, subtree: true });
    }
  }

  // Focus goes back to focusElementId if given, otherwise to the element that opened the dialog.
  // Without both (opened on page load, or the opener is gone) it goes to the main landmark, unless
  // the consumer has already moved it out of the dialog or another dialog is still open (D46).
  closeDialog(focusElementId) {
    this.stopDialogListeners();
    releasePage(this);
    const target = focusElementId ? document.getElementById(focusElementId) : this.opener;
    const active = document.activeElement;
    const focusLost =
      !active || active === document.body || !!this.refDiv.current?.contains(active);
    if (target?.isConnected) {
      target.focus();
    } else if (!focusElementId && focusLost && inertHolders.size === 0) {
      focusMainContent();
    }
    this.opener = null;
  }

  stopDialogListeners() {
    document.removeEventListener('keydown', this.handleKeyDownInDialog);
    this.contentObserver?.disconnect();
    this.contentObserver = null;
  }

  handleKeyDownInDialog(e) {
    const dialog = this.refDiv.current;
    if (e.key !== 'Tab' || !dialog) {
      return;
    }
    // Only the topmost open dialog keeps the focus
    const openDialogs = Array.from(
      document.querySelectorAll('[role="dialog"][aria-modal="true"]')
    ).filter(d => d.getClientRects().length > 0);
    if (openDialogs[openDialogs.length - 1] !== dialog) {
      return;
    }
    const focusables = tabStops(dialog);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const outside = !dialog.contains(active);
    if (e.shiftKey && (active === first || active === dialog || outside)) {
      e.preventDefault();
      (last || dialog).focus();
    } else if (!e.shiftKey && (active === last || outside)) {
      e.preventDefault();
      (first || dialog).focus();
    }
  }

  // The first heading inside the dialog names it (by id when it has one, otherwise by its text)
  updateDialogName() {
    const heading = this.refContent.current?.querySelector(NAMING_HEADING);
    const text = heading?.textContent.trim().replace(/\s+/g, ' ');
    const headingName = heading && text ? (heading.id ? { id: heading.id } : { text }) : null;
    const prev = this.state.headingName;
    if (prev?.id !== headingName?.id || prev?.text !== headingName?.text) {
      this.setState({ headingName });
    }
  }

  // A click outside the dialog closes it only when the press started outside it too (a text
  // selection dragged out of the dialog does not close it)
  handleBackdropMouseDown(event) {
    this.pressedOnBackdrop = event.target === event.currentTarget;
  }

  handleBackdropClick(event) {
    if (this.pressedOnBackdrop && event.target === event.currentTarget) {
      this.handleClose(event);
    }
    this.pressedOnBackdrop = false;
  }

  handleBodyKeyUp(event) {
    const { isOpen } = this.props;
    if (event.key === KEY_ESCAPE && isOpen) {
      this.handleClose(event);
    }
  }

  handleClose(event) {
    const { id, onClose, onManageDisableScrolling } = this.props;
    onManageDisableScrolling(id, false);
    releasePage(this);
    onClose(event);
  }

  handleResize() {
    this.vh = window.innerHeight * 0.01;
    window.document.documentElement.style.setProperty('--vh', `${this.vh}px`);
  }

  render() {
    const {
      children,
      className,
      scrollLayerClassName,
      closeButtonMessage,
      containerClassName,
      contentClassName,
      lightCloseButton,
      intl,
      isClosedClassName = css.isClosed,
      isOpen,
      usePortal,
      ariaLabelledBy,
      ariaLabel,
      closeOnOutsideClick,
    } = this.props;
    const { headingName } = this.state;
    const dialogNameProps = ariaLabelledBy
      ? { 'aria-labelledby': ariaLabelledBy }
      : headingName?.id
      ? { 'aria-labelledby': headingName.id }
      : {
          'aria-label':
            headingName?.text || ariaLabel || intl.formatMessage({ id: 'Modal.defaultLabel' }),
        };

    const closeModalMessage = intl.formatMessage({ id: 'Modal.closeModal' });
    const closeMessage = closeButtonMessage || intl.formatMessage({ id: 'Modal.close' });
    const closeButtonClasses = classNames(css.close, {
      [css.closeLight]: lightCloseButton,
    });
    const closeBtn = isOpen ? (
      <nav className={css.marketplaceModalCloseNav} aria-label={closeModalMessage}>
        <Button
          onClick={this.handleClose}
          rootClassName={closeButtonClasses}
          title={closeModalMessage}
          aria-expanded={isOpen}
        >
          <span className={css.closeText}>{closeMessage}</span>
          <IconClose rootClassName={css.closeIcon} ariaLabel={closeMessage} />
        </Button>
      </nav>
    ) : null;

    // Modal uses given styles to wrap child components.
    // If props doesn't contain isClosedClassName, styles default to css.isClosed
    // This makes it possible to create ModalInMobile on top of Modal where style modes are:
    // visible, hidden, or none (ModalInMobile's children are always visible on desktop layout.)
    const isOpenClass = usePortal ? css.isOpenInPortal : css.isOpenInPlace;
    const modalClass = isOpen ? isOpenClass : isClosedClassName;
    const classes = classNames(modalClass, className);
    const scrollLayerClasses = scrollLayerClassName || css.scrollLayer;
    const containerClasses = containerClassName || css.container;
    const portalRoot = this.state.portalRoot;
    const outsideClickProps =
      closeOnOutsideClick && isOpen
        ? { onMouseDown: this.handleBackdropMouseDown, onClick: this.handleBackdropClick }
        : {};

    // If you want to use Portal https://reactjs.org/docs/portals.html
    // you need to use 'userPortal' flag.
    // ModalInMobile component needs to use the old Modal without the portal
    // because it's relying that the content is rendered inside
    // the DOM hierarchy of the parent component unlike Modal inside Portal.

    return !usePortal ? (
      <div className={classes}>
        <div className={scrollLayerClasses}>
          <div className={containerClasses}>
            {closeBtn}
            <div className={classNames(contentClassName || css.content)}>{children}</div>
          </div>
        </div>
      </div>
    ) : portalRoot ? (
      <Portal portalRoot={portalRoot}>
        <div className={classes}>
          <div className={scrollLayerClasses} {...outsideClickProps}>
            <div
              className={classNames(containerClasses, css.focusedDiv)}
              ref={this.refDiv}
              tabIndex="-1"
              role="dialog"
              aria-modal="true"
              {...dialogNameProps}
            >
              {closeBtn}
              <div className={classNames(contentClassName || css.content)} ref={this.refContent}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </Portal>
    ) : null;
  }
}

export default injectIntl(ModalComponent);
